import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { UserConfig } from '@tunasong/manifest'
import manifest from '@tunasong/manifest'

// Define a type for the slice state
/**
 * Where the SPA is running. A local kernel (spec §8) serves the SPA, /api and /ws from one
 * loopback origin and has no cloud capabilities: no push, no search index, no release
 * notes entities. Features that only exist in the cloud read this and stand down rather
 * than log a 501 on every page load.
 */
export const hostKinds = ['cloud', 'local'] as const
export type HostKind = (typeof hostKinds)[number]

export interface AppConfig {
  baseDomain: string
  hostKind: HostKind
  apiBaseUrl: string
  /**
   * Origin stored files are read from by their storage key (`users/<id>/audio/<key>.peaks.json`).
   * The public CDN in the cloud; the kernel's `/api/storage` locally (spec §16.3).
   */
  storageBaseUrl: string
  /** WebSocket endpoint. Its own origin on a local host (spec §8.1), API Gateway in the cloud */
  wsBaseUrl: string
  streamingApiBaseUrl: string

  userConfig: UserConfig
  // user config is loaded from persisted storage, e.g., profile
  userConfigLoaded: boolean
}

// Define the initial state using that type
const initialState: AppConfig = {
  baseDomain: 'tunasong.com',
  hostKind: 'cloud',
  apiBaseUrl: 'https://tunasong.com/api',
  storageBaseUrl: 'https://tunasong.com',
  wsBaseUrl: 'wss://ws.api.tunasong.com',
  streamingApiBaseUrl: 'https://tunasong.com/stream',
  // Create a user config with the default values
  userConfig: manifest.userConfig.parse({ plugins: {} }),
  userConfigLoaded: false,
}

export const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    setHostKind: (state, action: PayloadAction<{ hostKind: HostKind }>) => {
      state.hostKind = action.payload.hostKind
    },
    setApiBaseUrl: (state, action: PayloadAction<{ apiBaseUrl: string }>) => {
      state.apiBaseUrl = action.payload.apiBaseUrl
    },
    setStorageBaseUrl: (state, action: PayloadAction<{ storageBaseUrl: string }>) => {
      state.storageBaseUrl = action.payload.storageBaseUrl
    },
    setWsBaseUrl: (state, action: PayloadAction<{ wsBaseUrl: string }>) => {
      state.wsBaseUrl = action.payload.wsBaseUrl
    },
    setStreamingApiBaseUrl: (state, action: PayloadAction<{ streamingApiBaseUrl: string }>) => {
      state.streamingApiBaseUrl = action.payload.streamingApiBaseUrl
    },
    updateUserConfig: (state, action: PayloadAction<Partial<UserConfig>>) => {
      state.userConfig = { ...state.userConfig, ...action.payload }
    },
    setUserConfigLoaded: (state, action: PayloadAction<boolean>) => {
      state.userConfigLoaded = action.payload
    },
  },
})
